import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBodyScrollLock } from '../../utils/scrollLock';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  showCloseButton?: boolean;
  /** Cuando true, el overlay y el drag no cierran el sheet. Usar para modales de bloqueo. */
  preventClose?: boolean;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  maxWidth = 'md',
  showCloseButton = true,
  preventClose = false,
}) => {
  // Bloquear scroll del body cuando el panel está abierto (ref-count compartido:
  // seguro ante superposición con otros modales/drawers).
  useBodyScrollLock(isOpen);

  const maxWidthClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
  };

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center overflow-hidden overscroll-none">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={preventClose ? undefined : onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal / Sheet Container */}
          <motion.div
            initial={{ y: '100%', opacity: 0.5, scale: 1 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            // v6.11.331 — Bloqueante: no se arrastra. Antes el gesto no lo
            // cerraba pero lo dejaba corrido fuera de pantalla, sin forma de
            // volver. El resto vuelve a su lugar si no llega a cerrarse.
            drag={preventClose ? false : 'y'}
            dragSnapToOrigin
            dragConstraints={{ top: 0 }}
            dragElastic={0.2}
            onDragEnd={(_, info) => {
              if (!preventClose && (info.offset.y > 150 || info.velocity.y > 500)) {
                onClose();
              }
            }}
            className={`
              bg-white w-full ${maxWidthClasses[maxWidth]} flex flex-col 
              max-h-[92dvh] sm:max-h-[85vh] shadow-2xl relative z-10
              rounded-t-3xl sm:rounded-2xl
              touch-none sm:touch-auto
            `}
          >
            {/* Drag Handle (Visual only for mobile) */}
            {!preventClose && (
              <div className="sm:hidden flex justify-center pt-3 pb-1 shrink-0 cursor-grab active:cursor-grabbing">
                <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
              </div>
            )}

            {/* Header */}
            <div className="bg-white text-gray-900 p-5 flex justify-between items-center shrink-0 border-b border-gray-100">
              <div className="flex-1 min-w-0">
                {typeof title === 'string' ? (
                  <h2 className="text-xl font-bold font-display truncate">{title}</h2>
                ) : (
                  title
                )}
              </div>
              {showCloseButton && (
                <button
                  onClick={onClose}
                  // p-2.5 + ícono de 24 = 44×44, el mínimo para tocar con el dedo.
                  className="p-2.5 hover:bg-gray-100 rounded-full transition-colors -mr-2.5"
                  aria-label="Cerrar"
                >
                  <X size={24} />
                </button>
              )}
            </div>

            {/* Content - Scrollable */}
            <div className="p-5 overflow-y-auto flex-1 custom-scrollbar min-h-0 overscroll-contain touch-auto">
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div className="p-4 border-t bg-gray-50 shrink-0">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};
